import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  TextField,
  MenuItem,
  Typography,
  Grid,
  InputAdornment,
  Paper,
  Chip,
  IconButton,
  Avatar,
  Alert,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Stack,
  alpha,
  useTheme,
} from '@mui/material'
import {
  ArrowBack,
  Save,
  Preview,
  CloudUpload,
  Category,
  LocalOffer,
  Inventory,
  Scale,
  Inventory2 as Package,
  Straighten,
  Link as LinkIcon,
  Edit,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Palette as ColorIcon,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { ProductStatus, GSTSlab, Variant } from '../types/product'
import { 
  useGetProductQuery, 
  useCreateProductMutation, 
  useUpdateProductMutation 
} from '../store/services/productApi'
import { useGetCategoriesQuery } from '../store/services/categoryApi'
import { productService } from '../services/productService'
import ImageUpload from '../components/ImageUpload'
import MultiImageUpload from '../components/MultiImageUpload'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  )
}

const ProductAddEdit: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { id } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const [activeTab, setActiveTab] = useState(0)
  
  // Variant management state
  const [variants, setVariants] = useState<Variant[]>([])
  const [showVariantDialog, setShowVariantDialog] = useState(false)
  const [editingVariant, setEditingVariant] = useState<(Variant & { index: number }) | null>(null)
  const [variantFormData, setVariantFormData] = useState<{
    sku: string
    attributes: Record<string, any>
    mrp: number
    sale_price: number
    wholesale_price: number
    purchase_price: number
    weight: number
    dimensions: { length: number; width: number; height: number }
    is_active: boolean
  }>({
    sku: '',
    attributes: {},
    mrp: 0,
    sale_price: 0,
    wholesale_price: 0,
    purchase_price: 0,
    weight: 0,
    dimensions: { length: 0, width: 0, height: 0 },
    is_active: true,
  })

  const isEdit = Boolean(id)
  
  // Fetch product data if editing
  const { data: product, isLoading: isLoadingProduct, error: loadError } = useGetProductQuery(id!, {
    skip: !isEdit,
  })
  
  // Fetch categories for dropdown
  const { data: categoriesData, isLoading: isLoadingCategories } = useGetCategoriesQuery({
    per_page: 100, // Get all categories
  })
  
  // Mutations
  const [createProduct, { isLoading: isCreating }] = useCreateProductMutation()
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation()
  
  const isLoading = isCreating || isUpdating
  
  // Extract categories list
  const categories = categoriesData?.items || []
  
  // Debug: Log categories data
  React.useEffect(() => {
    console.log('📦 Categories data:', {
      isLoading: isLoadingCategories,
      hasData: !!categoriesData,
      count: categories.length,
      categories: categories.slice(0, 3)
    })
  }, [categoriesData, isLoadingCategories, categories])

  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    item_code: '',
    sku: '',
    hsn_code: '',
    description: '',
    brand_id: '',
    brand_name: '',
    
    // Category
    category_id: '',
    status: ProductStatus.DRAFT,
    is_active: true,
    is_online_enabled: true,
    
    // B2B Specifications
    inner_pack_size: 1,
    carton_size: 1,
    moq: 1,
    moa: 0,
    set_size: 1,
    is_set_selling: false,
    
    // Physical Specifications
    weight: 0,
    length: 0,
    width: 0,
    height: 0,
    
    // Pricing
    mrp: 0,
    sale_price: 0,
    wholesale_price: 0,
    purchase_price: 0,
    discount_percentage: 0,
    gst_rate: 18,
    gst_slab: GSTSlab.EIGHTEEN,
    
    // NO inventory fields - stock is managed at warehouse level
    
    // Marketplace Links
    amazon_link: '',
    flipkart_link: '',
    meesho_link: '',
    
    // Images
    main_image: '',
    additional_images: [] as string[],
  })

  // Populate form when product data is loaded
  useEffect(() => {
    if (product && isEdit) {
      setFormData({
        name: product.name || '',
        item_code: product.item_code || '',
        sku: product.sku || '',
        hsn_code: product.hsn_code || '',
        description: product.description || '',
        brand_id: product.brand_id || '',
        brand_name: product.brand?.name || '',
        
        category_id: product.category_id || '',
        status: product.status || ProductStatus.DRAFT,
        is_active: product.is_active ?? true,
        is_online_enabled: product.is_online_enabled ?? true,
        
        inner_pack_size: product.inner_box_quantity || 1,
        carton_size: product.carton_quantity || 1,
        moq: product.moq || 1,
        moa: 0, // MOA doesn't exist in backend, keeping for UI only,
        set_size: product.set_size || 1,
        is_set_selling: product.is_set_selling || false,
        
        weight: Number(product.weight) || 0,
        length: product.dimensions?.length || 0,
        width: product.dimensions?.width || 0,
        height: product.dimensions?.height || 0,
        
        mrp: Number(product.mrp) || 0,
        sale_price: Number(product.sale_price) || 0,
        wholesale_price: Number(product.wholesale_price) || 0,
        purchase_price: Number(product.purchase_price) || 0,
        discount_percentage: 0,
        gst_rate: Number(product.gst_rate) || 18,
        gst_slab: product.gst_slab || GSTSlab.EIGHTEEN,
        
        // NO inventory fields - stock is managed at warehouse level
        
        amazon_link: (product.product_metadata?.marketplace_links as any)?.amazon || '',
        flipkart_link: (product.product_metadata?.marketplace_links as any)?.flipkart || '',
        meesho_link: (product.product_metadata?.marketplace_links as any)?.meesho || '',
        
        main_image: product.image || '',
        additional_images: product.images || [],
      })
      
      // Load variants if they exist
      if (product.variants && product.variants.length > 0) {
        setVariants(product.variants)
      }
    }
  }, [product, isEdit])
  
  // Show error if product failed to load
  useEffect(() => {
    if (loadError) {
      const err = loadError as any
      console.error('❌ Failed to load product:', {
        status: err?.status,
        data: err?.data,
        error: err?.error,
        originalStatus: err?.originalStatus
      })
      
      let errorMessage = 'Failed to load product data'
      if (err?.status === 404) {
        errorMessage = 'Product not found'
      } else if (err?.status === 401) {
        errorMessage = 'Please log in to view product'
      } else if (err?.data?.detail) {
        errorMessage = err.data.detail
      }
      
      enqueueSnackbar(errorMessage, { variant: 'error' })
    }
  }, [loadError, enqueueSnackbar])

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  // Calculate gross margin
  const calculateGrossMargin = (salePrice: number, purchasePrice: number): number => {
    if (salePrice <= 0 || purchasePrice < 0) return 0
    return ((salePrice - purchasePrice) / salePrice) * 100
  }

  // Get gross margin for display
  const grossMargin = React.useMemo(() => {
    return calculateGrossMargin(formData.sale_price, formData.purchase_price)
  }, [formData.sale_price, formData.purchase_price])

  const handleSave = async () => {
    try {
      // Validate pricing hierarchy: MRP >= sale_price >= wholesale_price >= purchase_price
      const pricingErrors: string[] = []
      
      if (formData.sale_price > formData.mrp) {
        pricingErrors.push('Sale price cannot exceed MRP')
      }
      
      if (formData.wholesale_price > formData.sale_price) {
        pricingErrors.push('Wholesale price cannot exceed sale price')
      }
      
      if (formData.purchase_price > formData.wholesale_price) {
        pricingErrors.push('Purchase price cannot exceed wholesale price')
      }
      
      if (formData.purchase_price < 0) {
        pricingErrors.push('Purchase price must be positive')
      }
      
      if (pricingErrors.length > 0) {
        enqueueSnackbar(pricingErrors.join('. '), { variant: 'error' })
        return
      }
      
      // Build marketplace links only if there are values
      const marketplaceLinks: any = {}
      if (formData.amazon_link) marketplaceLinks.amazon = formData.amazon_link
      if (formData.flipkart_link) marketplaceLinks.flipkart = formData.flipkart_link
      if (formData.meesho_link) marketplaceLinks.meesho = formData.meesho_link
      
      const productData: any = {
        name: formData.name,
        sku: formData.sku,
        item_code: formData.item_code,
        hsn_code: formData.hsn_code,
        description: formData.description,
        category_id: formData.category_id,
        
        mrp: formData.mrp,
        sale_price: formData.sale_price,
        wholesale_price: formData.wholesale_price,
        purchase_price: formData.purchase_price,
        gst_rate: formData.gst_rate,
        gst_slab: formData.gst_slab,
        
        // B2B Package Information
        moq: formData.moq || 1,
        inner_box_quantity: formData.inner_pack_size || null,
        carton_quantity: formData.carton_size || null,
        set_size: formData.set_size || 1,
        is_set_selling: formData.is_set_selling || false,
        
        // NO inventory fields - stock is managed at warehouse level
        
        status: formData.status,
        is_active: formData.is_active,
        is_online_enabled: formData.is_online_enabled,
        
        weight: formData.weight || 0,
        dimensions: {
          length: formData.length || 0,
          width: formData.width || 0,
          height: formData.height || 0,
        },
      }
      
      // Add optional fields
      if (formData.brand_id) {
        productData.brand_id = formData.brand_id
      }
      
      if (formData.main_image) {
        productData.image = formData.main_image
      }
      
      if (formData.additional_images && formData.additional_images.length > 0) {
        productData.images = formData.additional_images.filter(img => img && img.trim() !== '')
      }
      
      // Add marketplace links if any exist
      if (Object.keys(marketplaceLinks).length > 0) {
        productData.marketplace_links = marketplaceLinks
      }
      
      // Add variants if any exist
      if (variants.length > 0) {
        productData.variants = variants.map(variant => ({
          name: variant.name,
          item_code: variant.item_code || variant.sku,
          sku: variant.sku,
          attributes: variant.attributes || {},
          mrp: variant.mrp || null,
          sale_price: variant.sale_price || null,
          wholesale_price: variant.wholesale_price || null,
          purchase_price: variant.purchase_price || null,
          weight: variant.weight || null,
          dimensions: variant.dimensions || {},
          is_active: variant.is_active ?? true,
          is_online_enabled: variant.is_online_enabled ?? true,
        }))
      }
      
      if (isEdit && id) {
        await updateProduct({ id, data: productData }).unwrap()
        enqueueSnackbar('Product updated successfully!', { variant: 'success' })
      } else {
        await createProduct(productData).unwrap()
        enqueueSnackbar('Product created successfully!', { variant: 'success' })
      }
      
      navigate('/products')
    } catch (error: any) {
      console.error('Save error:', error)
      enqueueSnackbar(
        error?.data?.detail || 'Failed to save product', 
        { variant: 'error' }
      )
    }
  }

  const handlePreview = () => {
    if (isEdit && id) {
      const baseUrl = import.meta.env.VITE_CUSTOMER_PORTAL_URL
      if (baseUrl) {
        window.open(`${baseUrl}/products/${id}`, '_blank')
      } else {
        enqueueSnackbar('Front store URL not configured in VITE_CUSTOMER_PORTAL_URL', { variant: 'error' })
      }
    } else {
      enqueueSnackbar('Please save the product first to enable preview', { variant: 'info' })
    }
  }

  // Variant management functions
  const handleAddVariant = () => {
    setEditingVariant(null)
    setVariantFormData({
      sku: `${formData.sku}-V${variants.length + 1}`,
      attributes: {},
      mrp: 0, // Will inherit from product if 0
      sale_price: 0, // Will inherit from product if 0
      wholesale_price: 0, // Will inherit from product if 0
      purchase_price: 0, // Will inherit from product if 0
      weight: 0, // Will inherit from product if 0
      dimensions: { 
        length: 0, 
        width: 0, 
        height: 0 
      },
      is_active: true,
    })
    setShowVariantDialog(true)
  }

  const handleEditVariant = (variant: Variant, index: number) => {
    setEditingVariant({ ...variant, index })
    setVariantFormData({
      sku: variant.sku || '',
      attributes: variant.attributes || {},
      mrp: variant.mrp || 0,
      sale_price: variant.sale_price || 0,
      wholesale_price: variant.wholesale_price || 0,
      purchase_price: variant.purchase_price || 0,
      weight: variant.weight || 0,
      dimensions: { 
        length: variant.dimensions?.length || 0, 
        width: variant.dimensions?.width || 0, 
        height: variant.dimensions?.height || 0 
      },
      is_active: variant.is_active ?? true,
    })
    setShowVariantDialog(true)
  }

  const handleSaveVariant = () => {
    if (!variantFormData.sku.trim()) {
      enqueueSnackbar('SKU is required for variant', { variant: 'error' })
      return
    }

    // Check for duplicate SKU
    const isDuplicate = variants.some((v, index) => 
      v.sku === variantFormData.sku && 
      (!editingVariant || editingVariant.index !== index)
    )
    
    if (isDuplicate) {
      enqueueSnackbar('SKU already exists. Please use a unique SKU.', { variant: 'error' })
      return
    }

    // Generate variant name from attributes
    const attributeString = Object.entries(variantFormData.attributes || {})
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ')
    
    const variantName = attributeString 
      ? `${formData.name} (${attributeString})`
      : `${formData.name} - Variant ${variants.length + 1}`

    const newVariant: any = {
      ...variantFormData,
      name: variantName,
      item_code: variantFormData.sku, // Use SKU as item_code for variants
      // Only include pricing if different from 0 (will inherit from product otherwise)
      mrp: variantFormData.mrp || null,
      sale_price: variantFormData.sale_price || null,
      wholesale_price: variantFormData.wholesale_price || null,
      purchase_price: variantFormData.purchase_price || null,
      weight: variantFormData.weight || null,
      // Only include dimensions if they have values
      dimensions: (variantFormData.dimensions.length || variantFormData.dimensions.width || variantFormData.dimensions.height)
        ? variantFormData.dimensions
        : {},
    }

    if (editingVariant) {
      // Update existing variant
      const updatedVariants = [...variants]
      updatedVariants[editingVariant.index] = newVariant
      setVariants(updatedVariants)
      enqueueSnackbar('Variant updated successfully', { variant: 'success' })
    } else {
      // Add new variant
      setVariants([...variants, newVariant])
      enqueueSnackbar('Variant added successfully', { variant: 'success' })
    }

    setShowVariantDialog(false)
    setEditingVariant(null)
  }

  const handleDeleteVariant = (index: number) => {
    const updatedVariants = variants.filter((_, i) => i !== index)
    setVariants(updatedVariants)
    enqueueSnackbar('Variant deleted successfully', { variant: 'success' })
  }

  const handleDuplicateVariant = (variant: Variant) => {
    const duplicatedVariant = {
      ...variant,
      sku: `${variant.sku}-COPY`,
    }
    setVariants([...variants, duplicatedVariant])
    enqueueSnackbar('Variant duplicated successfully', { variant: 'success' })
  }

  const handleVariantFormChange = (field: string, value: any) => {
    if (field.startsWith('attributes.')) {
      const attrKey = field.replace('attributes.', '')
      setVariantFormData(prev => ({
        ...prev,
        attributes: {
          ...prev.attributes,
          [attrKey]: value
        }
      }))
    } else if (field.startsWith('dimensions.')) {
      const dimKey = field.replace('dimensions.', '')
      setVariantFormData(prev => ({
        ...prev,
        dimensions: {
          ...prev.dimensions,
          [dimKey]: value
        }
      }))
    } else {
      setVariantFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  const addAttributeField = () => {
    const attrName = prompt('Enter attribute name (e.g., Color, Size, Material):')
    if (attrName && attrName.trim()) {
      setVariantFormData(prev => ({
        ...prev,
        attributes: {
          ...prev.attributes,
          [attrName.trim()]: ''
        }
      }))
    }
  }

  const removeAttributeField = (attrKey: string) => {
    setVariantFormData(prev => {
      const newAttributes = { ...prev.attributes }
      delete newAttributes[attrKey]
      return {
        ...prev,
        attributes: newAttributes
      }
    })
  }

  // Categories are now fetched from API via useGetCategoriesQuery



  // Show loading state while fetching product
  if (isEdit && isLoadingProduct) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#FAFAFA'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
            Loading product data...
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA' }}>
      {/* Header */}
      <Paper 
        sx={{ 
          background: 'linear-gradient(135deg, #2196f3 0%, #0d47a1 100%)',
          color: 'white',
          p: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={() => navigate('/products')}
              sx={{ color: 'white' }}
            >
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {isEdit ? 'Edit Product' : 'Add New Product'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {isEdit ? 'Update product information' : 'Create a new product'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Preview />}
              onClick={handlePreview}
              sx={{ 
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                '&:hover': { 
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Preview
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={isLoading}
              sx={{ 
                bgcolor: '#f44336',
                '&:hover': { bgcolor: '#c62828' },
                boxShadow: '0 2px 8px rgba(244,67,54,0.3)'
              }}
            >
              {isLoading ? 'Saving...' : (isEdit ? 'Update' : 'Save')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Form Content */}
      <Box sx={{ p: 2 }}>
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={(_, newValue) => setActiveTab(newValue)}
              sx={{
                minHeight: 48,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: 100,
                  minHeight: 48,
                  py: 1,
                }
              }}
            >
              <Tab label="Basic Info" icon={<Edit />} iconPosition="start" />
              <Tab label="Variants" icon={<ColorIcon />} iconPosition="start" />
              <Tab label="Images" icon={<CloudUpload />} iconPosition="start" />
              <Tab label="B2B Specs" icon={<Package />} iconPosition="start" />
              <Tab label="Pricing" icon={<LocalOffer />} iconPosition="start" />
              <Tab label="Inventory" icon={<Inventory />} iconPosition="start" />
              <Tab label="Marketplace" icon={<LinkIcon />} iconPosition="start" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ p: 2 }}>
            {/* Basic Information Tab */}
            <TabPanel value={activeTab} index={0}>
              <Grid container spacing={2}>
                {/* Product Identity Card */}
                <Grid item xs={12}>
                  <Card elevation={1}>
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                          <Category />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>Product Identity</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Basic product information and identifiers
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Product Name"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            required
                            variant="outlined"
                            helperText="Enter the complete product name"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Brand"
                            value={formData.brand_name}
                            variant="outlined"
                            disabled
                            helperText="Brand information (read-only)"
                            InputProps={{
                              readOnly: true,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Item Code"
                            value={formData.item_code}
                            variant="outlined"
                            disabled
                            helperText="Auto-generated on save"
                            InputProps={{
                              readOnly: true,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="SKU"
                            value={formData.sku}
                            variant="outlined"
                            disabled
                            helperText="Auto-generated from item code and category"
                            InputProps={{
                              readOnly: true,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="HSN Code"
                            value={formData.hsn_code}
                            onChange={(e) => handleInputChange('hsn_code', e.target.value)}
                            required
                            variant="outlined"
                            helperText="HSN code for GST"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Product Description"
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            multiline
                            rows={4}
                            variant="outlined"
                            helperText="Detailed product description"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Card>
                </Grid>

                {/* Category & Status Card */}
                <Grid item xs={12}>
                  <Card elevation={1}>
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36 }}>
                          <Category />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>Category & Status</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Product categorization and visibility settings
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Autocomplete
                            options={categories}
                            getOptionLabel={(option) => option.name}
                            value={categories.find(cat => cat.id === formData.category_id) || null}
                            onChange={(_, value) => handleInputChange('category_id', value?.id || '')}
                            loading={isLoadingCategories}
                            disabled={isLoadingCategories}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Category"
                                required
                                variant="outlined"
                                helperText={isLoadingCategories ? "Loading categories..." : "Select product category"}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {isLoadingCategories ? <CircularProgress color="inherit" size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            noOptionsText={isLoadingCategories ? "Loading..." : "No categories found"}
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <TextField
                            select
                            fullWidth
                            label="Product Status"
                            value={formData.status}
                            onChange={(e) => handleInputChange('status', e.target.value)}
                            variant="outlined"
                          >
                            <MenuItem value={ProductStatus.DRAFT}>
                              <Chip label="Draft" color="warning" size="small" sx={{ mr: 1 }} />
                              Draft
                            </MenuItem>
                            <MenuItem value={ProductStatus.ACTIVE}>
                              <Chip label="Active" color="success" size="small" sx={{ mr: 1 }} />
                              Active
                            </MenuItem>
                            <MenuItem value={ProductStatus.ARCHIVED}>
                              <Chip label="Archived" color="default" size="small" sx={{ mr: 1 }} />
                              Archived
                            </MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={formData.is_active}
                                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                              />
                            }
                            label="Active Product"
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={formData.is_online_enabled}
                                onChange={(e) => handleInputChange('is_online_enabled', e.target.checked)}
                              />
                            }
                            label="Online Enabled"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Variants Tab */}
            <TabPanel value={activeTab} index={1}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card>
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main', mr: 1.5, width: 36, height: 36 }}>
                          <ColorIcon />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight={600}>Product Variants</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Create variants for different sizes, colors, or configurations
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={handleAddVariant}
                          sx={{ textTransform: 'none', fontWeight: 500 }}
                        >
                          Add Variant
                        </Button>
                      </Box>

                      {variants.length === 0 ? (
                        <Paper 
                          variant="outlined" 
                          sx={{ 
                            textAlign: 'center', 
                            py: 6, 
                            backgroundColor: 'grey.50',
                            borderStyle: 'dashed',
                            borderColor: 'grey.300'
                          }}
                        >
                          <ColorIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No variants created yet
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                            Create variants for different sizes, colors, or configurations. 
                            Each variant can have its own SKU, pricing, and inventory.
                          </Typography>
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<AddIcon />}
                            onClick={handleAddVariant}
                            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
                          >
                            Create First Variant
                          </Button>
                        </Paper>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ backgroundColor: '#0d47a1', color: '#ffffff', fontWeight: 600 }}>
                                  SKU & Attributes
                                </TableCell>
                                <TableCell sx={{ backgroundColor: '#0d47a1', color: '#ffffff', fontWeight: 600 }}>
                                  Pricing
                                </TableCell>
                                <TableCell sx={{ backgroundColor: '#0d47a1', color: '#ffffff', fontWeight: 600 }}>
                                  Stock
                                </TableCell>
                                <TableCell sx={{ backgroundColor: '#0d47a1', color: '#ffffff', fontWeight: 600 }}>
                                  Actions
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {variants.map((variant, index) => (
                                <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.04)' } }}>
                                  <TableCell>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {variant.sku}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {variant.attributes && Object.keys(variant.attributes).length > 0
                                          ? Object.entries(variant.attributes).map(([key, value]) => 
                                              `${key}: ${value}`
                                            ).join(', ')
                                          : 'Default variant'
                                        }
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                                      Sale: ₹{variant.sale_price || formData.sale_price}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Wholesale: ₹{variant.wholesale_price || formData.wholesale_price}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      Managed per warehouse
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Stack direction="row" spacing={0.5}>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleEditVariant(variant, index)}
                                        title="Edit variant"
                                      >
                                        <Edit />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleDuplicateVariant(variant)}
                                        title="Duplicate variant"
                                      >
                                        <CopyIcon />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDeleteVariant(index)}
                                        title="Delete variant"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}

                      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                        <Typography variant="body2">
                          <strong>Variants allow you to:</strong> Create different configurations (sizes, colors, materials), 
                          set individual pricing and inventory levels, and generate unique SKUs for each variation.
                        </Typography>
                      </Alert>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Images Tab */}
            <TabPanel value={activeTab} index={2}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card elevation={1}>
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'info.main', width: 36, height: 36 }}>
                          <CloudUpload />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>Product Images</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Upload high-quality product images (recommended: 800x800px)
                          </Typography>
                        </Box>
                      </Box>
                      
                      {!id && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Please save the product first before uploading images.
                        </Alert>
                      )}
                      
                      <Grid container spacing={2}>
                        {/* Main Image Upload */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                            Main Product Image
                          </Typography>
                          <ImageUpload
                            label="Main Product Image"
                            value={formData.main_image}
                            onChange={(url) => handleInputChange('main_image', url)}
                            onUpload={id ? async (file) => {
                              const result = await productService.uploadProductImage(id, file, true)
                              // Response structure: { id, original, sizes: { medium: { jpeg, webp } } }
                              const imageUrl = result.sizes?.medium?.jpeg || result.original
                              return { url: imageUrl }
                            } : undefined}
                            maxSizeMB={5}
                            recommendedSize="800x800px"
                            disabled={!id}
                            showPreview={true}
                          />
                        </Grid>

                        {/* Additional Images */}
                        <Grid item xs={12}>
                          <MultiImageUpload
                            label="Additional Images (Optional)"
                            values={formData.additional_images}
                            onChange={(urls) => handleInputChange('additional_images', urls)}
                            onUpload={id ? async (file) => {
                              const result = await productService.uploadProductImage(id, file, false)
                              // Response structure: { id, original, sizes: { medium: { jpeg, webp } } }
                              const imageUrl = result.sizes?.medium?.jpeg || result.original
                              return { url: imageUrl }
                            } : undefined}
                            maxImages={6}
                            maxSizeMB={5}
                            disabled={!id}
                          />
                        </Grid>

                        {/* Image Guidelines */}
                        <Grid item xs={12}>
                          <Alert severity="info">
                            <Typography variant="subtitle2" gutterBottom>
                              Image Guidelines:
                            </Typography>
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                              <li>Use high-resolution images (minimum 800x800px)</li>
                              <li>Ensure good lighting and clear product visibility</li>
                              <li>Show different angles and key features</li>
                              <li>Use white or neutral backgrounds for consistency</li>
                              <li>First image will be used as the main product image</li>
                            </ul>
                          </Alert>
                        </Grid>
                      </Grid>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* B2B Specifications Tab */}
            <TabPanel value={activeTab} index={3}>
              <Grid container spacing={2}>
                {/* Packaging Information */}
                <Grid item xs={12}>
                  <Card elevation={1}>
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ mb: 4, p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 2, border: '1px dashed', borderColor: 'success.main' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'success.dark', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalOffer fontSize="small" /> Set Selling Configuration (Multiples)
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={6}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={formData.is_set_selling}
                                  onChange={(e) => handleInputChange('is_set_selling', e.target.checked)}
                                  color="success"
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>Enable Set Selling</Typography>
                                  <Typography variant="caption" color="text.secondary">Enforce sales in multiples of a set size</Typography>
                                </Box>
                              }
                            />
                          </Grid>
                          {formData.is_set_selling && (
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Set Size"
                                type="number"
                                size="small"
                                value={formData.set_size}
                                onChange={(e) => handleInputChange('set_size', Number(e.target.value))}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">units</InputAdornment>,
                                }}
                                helperText="Users can only buy in multiples of this number"
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'success.main', width: 36, height: 36 }}>
                          <Package />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>B2B Packaging Specifications</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Packaging details for bulk orders and distribution
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Inner Pack Size"
                            type="number"
                            value={formData.inner_pack_size}
                            onChange={(e) => handleInputChange('inner_pack_size', Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">pcs</InputAdornment>,
                            }}
                            helperText={
                              formData.is_set_selling && formData.set_size > 1
                                ? `Total pcs per inner box — e.g. 6 sets × ${formData.set_size} pcs = ${6 * formData.set_size} pcs`
                                : 'Total pcs per inner box (always enter in pcs)'
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Carton Size (CTN)"
                            type="number"
                            value={formData.carton_size}
                            onChange={(e) => handleInputChange('carton_size', Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">pcs</InputAdornment>,
                            }}
                            helperText={
                              formData.is_set_selling && formData.set_size > 1
                                ? `Enter TOTAL pcs in carton — e.g. 100 sets × ${formData.set_size} pcs = ${100 * formData.set_size} pcs. System auto-calculates sets.`
                                : 'Enter TOTAL pcs per carton (always in pcs, not boxes/sets)'
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="MOQ (Minimum Order Quantity)"
                            type="number"
                            value={formData.moq}
                            onChange={(e) => handleInputChange('moq', Number(e.target.value))}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  {formData.is_set_selling && formData.set_size > 1 ? 'boxes/sets' : 'pcs'}
                                </InputAdornment>
                              ),
                            }}
                            helperText={
                              formData.is_set_selling && formData.set_size > 1
                                ? `Enter MOQ in boxes/sets — e.g. MOQ 2 means customer must buy at least 2 sets (${2 * formData.set_size} pcs)`
                                : 'Enter MOQ in pcs — e.g. MOQ 12 means customer must buy at least 12 pcs'
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="MOA (Minimum Order Amount)"
                            type="number"
                            value={formData.moa}
                            onChange={(e) => handleInputChange('moa', Number(e.target.value))}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            helperText="Minimum order value required"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Card>
                </Grid>

                {/* Physical Specifications */}
                <Grid item xs={12}>
                  <Card elevation={1}>
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'warning.main', width: 36, height: 36 }}>
                          <Straighten />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>Physical Specifications</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Dimensions and weight information for shipping
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Weight"
                            type="number"
                            value={formData.weight}
                            onChange={(e) => handleInputChange('weight', Number(e.target.value))}
                            InputProps={{
                              startAdornment: <Scale sx={{ mr: 1, color: 'action.active' }} />,
                              endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                            }}
                            helperText="Product weight in kilograms"
                            inputProps={{ step: '0.01' }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Length"
                            type="number"
                            value={formData.length}
                            onChange={(e) => handleInputChange('length', Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                            }}
                            helperText="Length in centimeters"
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Width"
                            type="number"
                            value={formData.width}
                            onChange={(e) => handleInputChange('width', Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                            }}
                            helperText="Width in centimeters"
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Height"
                            type="number"
                            value={formData.height}
                            onChange={(e) => handleInputChange('height', Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                            }}
                            helperText="Height in centimeters"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Pricing Tab */}
            <TabPanel value={activeTab} index={4}>
              <Grid container spacing={2}>
                {/* Basic Pricing */}
                <Grid item xs={12}>
                  <Card elevation={1}>
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'success.main', width: 36, height: 36 }}>
                          <LocalOffer />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>B2B Pricing Structure</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Set comprehensive pricing for different customer tiers
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="MRP (Maximum Retail Price)"
                            type="number"
                            value={formData.mrp}
                            onChange={(e) => handleInputChange('mrp', Number(e.target.value))}
                            required
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            helperText="Market price for end customers"
                            inputProps={{ step: '0.01' }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Sale Price"
                            type="number"
                            value={formData.sale_price}
                            onChange={(e) => handleInputChange('sale_price', Number(e.target.value))}
                            required
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            helperText="Regular selling price"
                            inputProps={{ step: '0.01' }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Wholesale Price"
                            type="number"
                            value={formData.wholesale_price}
                            onChange={(e) => handleInputChange('wholesale_price', Number(e.target.value))}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            helperText="Price for wholesale customers"
                            inputProps={{ step: '0.01' }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Purchase Price"
                            type="number"
                            value={formData.purchase_price}
                            onChange={(e) => handleInputChange('purchase_price', Number(e.target.value))}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            helperText="Your cost price"
                            inputProps={{ step: '0.01' }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Discount Percentage"
                            type="number"
                            value={formData.discount_percentage}
                            onChange={(e) => handleInputChange('discount_percentage', Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                            helperText="Discount from MRP"
                            inputProps={{ step: '0.01', max: '100' }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            select
                            fullWidth
                            label="GST Rate"
                            value={formData.gst_rate}
                            onChange={(e) => handleInputChange('gst_rate', Number(e.target.value))}
                            helperText="Applicable GST rate"
                          >
                            <MenuItem value={0}>0% - Essential goods</MenuItem>
                            <MenuItem value={5}>5% - Basic necessities</MenuItem>
                            <MenuItem value={12}>12% - Standard goods</MenuItem>
                            <MenuItem value={18}>18% - Most products</MenuItem>
                            <MenuItem value={28}>28% - Luxury items</MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>
                    </Box>
                  </Card>
                </Grid>

                {/* Pricing Summary */}
                <Grid item xs={12}>
                  <Card elevation={1} sx={{ bgcolor: 'primary.50' }}>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Pricing Summary
                      </Typography>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} md={2.4}>
                          <Box textAlign="center" p={1.5} bgcolor="white" borderRadius={1}>
                            <Typography variant="h6" color="primary">
                              ₹{formData.mrp.toLocaleString('en-IN')}
                            </Typography>
                            <Typography variant="caption">MRP</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                          <Box textAlign="center" p={1.5} bgcolor="white" borderRadius={1}>
                            <Typography variant="h6" color="success.main">
                              ₹{formData.sale_price.toLocaleString('en-IN')}
                            </Typography>
                            <Typography variant="caption">Sale Price</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                          <Box textAlign="center" p={1.5} bgcolor="white" borderRadius={1}>
                            <Typography variant="h6" color="info.main">
                              ₹{formData.wholesale_price.toLocaleString('en-IN')}
                            </Typography>
                            <Typography variant="caption">Wholesale</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                          <Box textAlign="center" p={1.5} bgcolor="white" borderRadius={1}>
                            <Typography variant="h6" color="warning.main">
                              {formData.gst_rate}%
                            </Typography>
                            <Typography variant="caption">GST Rate</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                          <Box textAlign="center" p={1.5} bgcolor="white" borderRadius={1} data-testid="gross-margin">
                            <Typography variant="h6" color={grossMargin > 0 ? 'success.main' : 'error.main'}>
                              {grossMargin.toFixed(2)}%
                            </Typography>
                            <Typography variant="caption">Gross Margin</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                      <Alert severity="info" sx={{ mt: 1.5 }}>
                        <Typography variant="caption">
                          <strong>Gross Margin:</strong> Calculated as (Sale Price - Purchase Price) / Sale Price × 100. 
                          This represents your profit margin before other costs.
                        </Typography>
                      </Alert>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Inventory Tab */}
            <TabPanel value={activeTab} index={5}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card elevation={1}>
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'info.main', width: 36, height: 36 }}>
                          <Inventory />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>Inventory Management</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Stock is managed at warehouse level
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Alert severity="info">
                            <Typography variant="body2">
                              <strong>Stock Management:</strong> Stock is managed per warehouse. 
                              After creating the product, use the Inventory page to add stock to specific warehouses.
                            </Typography>
                          </Alert>
                        </Grid>
                      </Grid>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Marketplace Links Tab */}
            <TabPanel value={activeTab} index={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card elevation={1}>
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'warning.main', width: 36, height: 36 }}>
                          <LinkIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>Marketplace Links</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Links to this product on other platforms for price comparison
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Amazon Link"
                            value={formData.amazon_link}
                            onChange={(e) => handleInputChange('amazon_link', e.target.value)}
                            placeholder="https://amazon.in/..."
                            helperText="Amazon product page URL"
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Flipkart Link"
                            value={formData.flipkart_link}
                            onChange={(e) => handleInputChange('flipkart_link', e.target.value)}
                            placeholder="https://flipkart.com/..."
                            helperText="Flipkart product page URL"
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Meesho Link"
                            value={formData.meesho_link}
                            onChange={(e) => handleInputChange('meesho_link', e.target.value)}
                            placeholder="https://meesho.com/..."
                            helperText="Meesho product page URL"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>
          </Box>
        </Card>
      </Box>

      {/* Variant Management Dialog */}
      <Dialog 
        open={showVariantDialog} 
        onClose={() => setShowVariantDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #2196f3 0%, #0d47a1 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ColorIcon />
            <Typography variant="h6">
              {editingVariant ? 'Edit Product Variant' : 'Add New Product Variant'}
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setShowVariantDialog(false)} 
            sx={{ color: 'white' }}
          >
            <ArrowBack />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Basic Variant Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Edit />
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Variant SKU"
                value={variantFormData.sku}
                onChange={(e) => handleVariantFormChange('sku', e.target.value)}
                required
                helperText="Unique identifier for this variant"
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={variantFormData.is_active}
                    onChange={(e) => handleVariantFormChange('is_active', e.target.checked)}
                  />
                }
                label="Active Variant"
              />
            </Grid>

            {/* Variant Attributes */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ColorIcon />
                  Variant Attributes
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addAttributeField}
                  sx={{ textTransform: 'none' }}
                >
                  Add Attribute
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {!variantFormData.attributes || Object.keys(variantFormData.attributes).length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Add attributes like Color, Size, Material to differentiate this variant.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {variantFormData.attributes && Object.entries(variantFormData.attributes).map(([key, value]) => (
                    <Grid item xs={12} md={6} key={key}>
                      <TextField
                        fullWidth
                        label={key}
                        value={value}
                        onChange={(e) => handleVariantFormChange(`attributes.${key}`, e.target.value)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => removeAttributeField(key)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>

            {/* Pricing Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalOffer />
                Pricing (Leave empty to inherit from main product)
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="MRP"
                type="number"
                value={variantFormData.mrp || ''}
                onChange={(e) => handleVariantFormChange('mrp', Number(e.target.value) || 0)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                helperText={`Default: ₹${formData.mrp}`}
                inputProps={{ step: '0.01' }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sale Price"
                type="number"
                value={variantFormData.sale_price || ''}
                onChange={(e) => handleVariantFormChange('sale_price', Number(e.target.value) || 0)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                helperText={`Default: ₹${formData.sale_price}`}
                inputProps={{ step: '0.01' }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Wholesale Price"
                type="number"
                value={variantFormData.wholesale_price || ''}
                onChange={(e) => handleVariantFormChange('wholesale_price', Number(e.target.value) || 0)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                helperText={`Default: ₹${formData.wholesale_price}`}
                inputProps={{ step: '0.01' }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Purchase Price"
                type="number"
                value={variantFormData.purchase_price || ''}
                onChange={(e) => handleVariantFormChange('purchase_price', Number(e.target.value) || 0)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                helperText={`Default: ₹${formData.purchase_price}`}
                inputProps={{ step: '0.01' }}
              />
            </Grid>

            {/* Inventory Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Inventory />
                Inventory Management
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Stock Management:</strong> Stock is managed per warehouse. 
                  After creating the variant, use the Inventory page to add stock to specific warehouses.
                </Typography>
              </Alert>
            </Grid>

            {/* Physical Specifications */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Scale />
                Physical Specifications (Optional)
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Weight"
                type="number"
                value={variantFormData.weight || ''}
                onChange={(e) => handleVariantFormChange('weight', Number(e.target.value) || 0)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                }}
                helperText={`Default: ${formData.weight}kg`}
                inputProps={{ step: '0.01' }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Length"
                type="number"
                value={variantFormData.dimensions.length || ''}
                onChange={(e) => handleVariantFormChange('dimensions.length', Number(e.target.value) || 0)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                }}
                helperText={`Default: ${formData.length}cm`}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Width"
                type="number"
                value={variantFormData.dimensions.width || ''}
                onChange={(e) => handleVariantFormChange('dimensions.width', Number(e.target.value) || 0)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                }}
                helperText={`Default: ${formData.width}cm`}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Height"
                type="number"
                value={variantFormData.dimensions.height || ''}
                onChange={(e) => handleVariantFormChange('dimensions.height', Number(e.target.value) || 0)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                }}
                helperText={`Default: ${formData.height}cm`}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setShowVariantDialog(false)} 
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveVariant}
            variant="contained" 
            color="secondary"
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            {editingVariant ? 'Update Variant' : 'Add Variant'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProductAddEdit